import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { BASE_URL } from '../config/api';
import SEOHead from '../components/SEOHead';
import './AboutUs.css';

// react-snap prerenders with userAgent "ReactSnap", using an old bundled
// Chromium that can crash parsing modern syntax in third-party embed scripts
// (confirmed with Google Maps' embed script — see ContactMap.js). YouTube's
// embed player is the same class of risk and untested against it, so skip it
// during prerender too; real visitors still get the live embed on hydration.
const isPrerender =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const AboutUs = () => {
  const { t, i18n } = useTranslation();
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchAboutUs();
  }, []);

  const fetchAboutUs = async () => {
    try {
      const response = await api.get('/content/about-us/');
      setAboutData(response.data);
    } catch (err) {
      setError('Failed to load About Us page');
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedField = (field) => {
    if (!aboutData) return '';
    const langField = `${field}_${i18n.language}`;
    return aboutData[langField] || aboutData[`${field}_en`];
  };
  
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}${imagePath.startsWith('/') ? '' : '/media/'}${imagePath}`;
  };

  const nextImage = () => {
    if (aboutData?.images && aboutData.images.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === aboutData.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (aboutData?.images && aboutData.images.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? aboutData.images.length - 1 : prev - 1
      );
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (aboutData?.images && aboutData.images.length > 1) {
      const interval = setInterval(() => {
        nextImage();
      }, 5000); // Change image every 5 seconds

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aboutData?.images, currentImageIndex]);

  if (loading) {
    return <div className="container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (error) {
    return <div className="container"><div className="error-message">{error}</div></div>;
  }

  const currentImage = aboutData?.images?.[currentImageIndex];
  const getImageCaption = (image) => {
    const lang = i18n.language;
    return image[`caption_${lang}`] || image.caption_en || '';
  };

  return (
    <>
      <SEOHead
        title="About GFM Studio | Film & Broadcast Team"
        description="Learn about GFM Studio, our story, and the film and broadcast team behind our productions."
        path="/about"
      />
      {/* Split Layout: Image Slider on Left, Content on Right */}
      <div className="about-split-container">
        {/* Left Half - Image Carousel */}
        <div className="about-left-section">
            {aboutData?.images && aboutData.images.length > 0 && (
              <div className="carousel-container">
                <div className="carousel-main">
                  <img 
                    src={getImageUrl(currentImage.image)} 
                    alt={getImageCaption(currentImage)}
                    className="carousel-image"
                  />
                  {currentImage && getImageCaption(currentImage) && (
                    <div className="carousel-caption">
                      {getImageCaption(currentImage)}
                    </div>
                  )}
                </div>

                {aboutData.images.length > 1 && (
                  <>
                    <button className="carousel-btn prev" onClick={prevImage}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                      </svg>
                    </button>
                    <button className="carousel-btn next" onClick={nextImage}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                      </svg>
                    </button>

                    <div className="carousel-indicators">
                      {aboutData.images.map((_, index) => (
                        <button
                          key={index}
                          className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                          onClick={() => goToImage(index)}
                          aria-label={`Go to image ${index + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

        {/* Right Half - Text Content */}
        <div className="about-right-section">
          <div className="about-content">
            <h1 className="about-title">{getLocalizedField('title')}</h1>
            <div className="content-text">
              {getLocalizedField('content').split('\n').map((paragraph, index) => (
                paragraph.trim() && <p key={index}>{paragraph}</p>
              ))}
            </div>
            
            {/* YouTube Video */}
            {aboutData?.youtube_embed_url && !isPrerender && (
              <div className="video-section">
                <div className="video-container">
                  <iframe
                    src={aboutData.youtube_embed_url}
                    title="About GFM Studio"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutUs;

